import { sphereGeometry, uToPhi, vToTheta } from "@faro-lotv/lotv";
import type { CachedWorldTransform } from "@faro-lotv/project-source";
import type { BoundingBoxCoordinates } from "@faro-lotv/service-wires";
import {
  Float32BufferAttribute,
  Quaternion,
  Vector3,
  type BufferGeometry,
  type Vector3Tuple,
} from "three";

/** The number of slices used to cut the sphere geometry in both azimuthal and polar directions */
const SLICES = 16;

/**
 * Build a sphere geometry segment that covers only the given bounding box region,
 * including the UV coordinates used for colorization.
 *
 * Converts the normalized 2D coordinates [0,1] to spherical coordinates (phi and
 * theta), where phi represents horizontal rotation and theta the vertical angle.
 *
 * @param boundingBox The normalized [0,1] bounding box coordinates.
 * @returns A sphere geometry segment covering the bounding box region.
 */
export function boundingBoxToSphereGeometry(
  boundingBox: BoundingBoxCoordinates,
): BufferGeometry {
  const theta0 = yToTheta(boundingBox.y1);
  const theta1 = yToTheta(boundingBox.y2);
  const phi0 = xToPhi(boundingBox.x1);
  let phi1 = xToPhi(boundingBox.x2);

  // We assume that x1 < x2, and therefore phi0 > phi1. If this is not the case,
  // then we assume that the bounding box crosses the pano image wraparound edge,
  // therefore we exploit the periodicity of phi coordinates by subtracting 2PI to phi1.
  // This will make sure that bounding boxes spanning across the border of the pano image are rendered correctly.
  if (phi0 < phi1) {
    phi1 -= 2 * Math.PI;
  }

  const geometry = sphereGeometry(
    1,
    SLICES,
    SLICES,
    Math.min(phi0, phi1),
    Math.abs(phi0 - phi1),
    Math.min(theta0, theta1),
    Math.abs(theta0 - theta1),
  );

  // Create uv coordinates for the colorization
  const uv = [];
  for (let i = 0; i <= SLICES; ++i) {
    const v = i / SLICES;
    for (let j = 0; j <= SLICES; ++j) {
      const u = j / SLICES;
      uv.push(1 - u, v);
    }
  }
  geometry.setAttribute("uv", new Float32BufferAttribute(uv, 2));

  return geometry;
}

/**
 * Compute the local-space point on the unit sphere surface for the top-left
 * corner of the bounding box. Used as the HTML anchor for the label.
 *
 * @param boundingBox The normalized [0,1] bounding box coordinates.
 * @returns The corner position on the unit sphere, in pano-local space.
 */
export function boundingBoxTopLeftLocal(
  boundingBox: BoundingBoxCoordinates,
): Vector3Tuple {
  return imagePointToSphere(boundingBox.x1, boundingBox.y1);
}

/**
 * Convert a point of the panorama image to the point it maps to on the unit sphere.
 *
 * @param x The normalized [0,1] horizontal coordinate, 0 being the left of the image.
 * @param y The normalized [0,1] vertical coordinate, 0 being the top of the image.
 * @returns The position on the unit sphere, in pano-local space.
 */
export function imagePointToSphere(x: number, y: number): Vector3Tuple {
  const phi = xToPhi(x);
  const theta = yToTheta(y);
  return [
    Math.cos(phi) * Math.cos(theta),
    Math.sin(phi) * Math.cos(theta),
    Math.sin(theta),
  ];
}

/**
 * Compute the world-space position of the bounding box center, on the unit
 * sphere surface, by combining the local sphere point with the pano transform.
 *
 * @param boundingBox The normalized [0,1] bounding box coordinates.
 * @param transform The pano camera transform to apply.
 * @returns The center position on the unit sphere, in world space.
 */
export function boundingBoxCenterWorld(
  boundingBox: BoundingBoxCoordinates,
  transform: CachedWorldTransform,
): Vector3Tuple {
  const centerX = (boundingBox.x1 + boundingBox.x2) / 2;
  const centerY = (boundingBox.y1 + boundingBox.y2) / 2;
  return new Vector3(...imagePointToSphere(centerX, centerY))
    .applyQuaternion(new Quaternion(...transform.quaternion))
    .add(new Vector3(...transform.position))
    .toArray();
}

function yToTheta(y: number): number {
  return vToTheta(1 - y * 2);
}
function xToPhi(x: number): number {
  return uToPhi(x * 2 - 1);
}
